/**
 * C4/C5/D4 · The pitch surface: target notes as bars, sung pitch as a trace.
 *  - mode "scroll": live player — time scrolls, "now" line at 30% width
 *  - mode "full":   review — whole exercise fits, playhead cursor
 * Colors: warm-neutral targets; primary trace terracotta #d85a30 (brand);
 * compare trace blue #2a78d6 (validated pair, D9).
 */

import { useEffect, useRef } from 'react';
import { midiToName, type RenderedExercise, type TracePoint } from '@solfo/shared';

const TARGET = '#d6cdc4';
const TARGET_ACTIVE = '#5c554e';
const TRACE_A = '#d85a30';
const TRACE_B = '#2a78d6';
const NOW = '#2c2c2a';

export interface PitchCanvasProps {
  rendered: RenderedExercise;
  trace: TracePoint[];
  traceB?: TracePoint[];
  currentTime: number;
  mode: 'scroll' | 'full';
  height?: number;
  windowSec?: number;
  /** false = ear-training run: keep the grid + trace but hide target bars. */
  showTargets?: boolean;
}

export default function PitchCanvas({
  rendered,
  trace,
  traceB,
  currentTime,
  mode,
  height = 240,
  windowSec = 6,
  showTargets = true,
}: PitchCanvasProps) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = height;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const lo = rendered.lowestMidi - 4;
    const hi = rendered.highestMidi + 4;
    const y = (midi: number) => h - ((midi - lo) / (hi - lo)) * h;

    let x: (t: number) => number;
    if (mode === 'scroll') {
      const left = currentTime - windowSec * 0.3;
      x = (t) => ((t - left) / windowSec) * w;
    } else {
      const total = rendered.totalSec || 1;
      x = (t) => (t / total) * w;
    }

    // octave grid (Cs)
    ctx.font = '10px system-ui';
    for (let m = Math.ceil(lo / 12) * 12; m <= hi; m += 12) {
      ctx.strokeStyle = '#f3efeb';
      ctx.beginPath();
      ctx.moveTo(0, y(m));
      ctx.lineTo(w, y(m));
      ctx.stroke();
      ctx.fillStyle = '#a89e94';
      ctx.fillText(midiToName(m), 4, y(m) - 3);
    }

    // target notes
    for (const note of showTargets ? rendered.notes : []) {
      if (note.midi === null) {
        // breathing break — show a soft hint so the student sees it coming
        if (note.syllable) {
          const x0 = x(note.startSec);
          const x1 = x(note.startSec + note.durSec);
          if (x1 > -20 && x0 < w + 20 && x1 - x0 > 34) {
            ctx.fillStyle = '#a89e94';
            ctx.font = 'italic 10px system-ui';
            ctx.textAlign = 'center';
            ctx.fillText(`( ${note.syllable} )`, (x0 + x1) / 2, h / 2);
            ctx.textAlign = 'left';
            ctx.font = '10px system-ui';
          }
        }
        continue;
      }
      const x0 = x(note.startSec);
      const x1 = x(note.startSec + note.durSec);
      if (x1 < -20 || x0 > w + 20) continue;
      const active = currentTime >= note.startSec && currentTime < note.startSec + note.durSec;
      ctx.strokeStyle = active ? TARGET_ACTIVE : TARGET;
      ctx.lineWidth = 8;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(x0 + 4, y(note.midi));
      if (note.glideToMidi !== null) ctx.lineTo(x1 - 4, y(note.glideToMidi));
      else ctx.lineTo(Math.max(x0 + 5, x1 - 4), y(note.midi));
      ctx.stroke();
      if (note.syllable && x1 - x0 > 26) {
        ctx.fillStyle = active ? '#47413c' : '#a89e94';
        ctx.fillText(note.syllable, x0 + 6, y(note.midi) + 16);
      }
    }

    const traceTo = (points: TracePoint[]) => {
      ctx.beginPath();
      let pen = false;
      let prev: TracePoint | null = null;
      for (const p of points) {
        if (mode === 'scroll' && p.t > currentTime) break;
        const px = x(p.t);
        if (px < -10 || px > w + 10) {
          pen = false;
          prev = p;
          continue;
        }
        // lift the pen across gaps (breaths) or octave jumps
        if (!pen || (prev && (p.t - prev.t > 0.25 || Math.abs(p.midi - prev.midi) > 7))) {
          ctx.moveTo(px, y(p.midi));
          pen = true;
        } else {
          ctx.lineTo(px, y(p.midi));
        }
        prev = p;
      }
    };
    // two passes: a soft wide halo underneath, then the crisp line on top
    const drawTrace = (points: TracePoint[], color: string, halo: string) => {
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      traceTo(points);
      ctx.strokeStyle = halo;
      ctx.lineWidth = 7;
      ctx.stroke();
      traceTo(points);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();
    };
    if (traceB) drawTrace(traceB, TRACE_B, 'rgba(42,120,214,0.14)');
    drawTrace(trace, TRACE_A, 'rgba(216,90,48,0.16)');

    // now line / playhead
    const nowX = x(currentTime);
    ctx.strokeStyle = NOW;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(nowX, 0);
    ctx.lineTo(nowX, h);
    ctx.stroke();
    ctx.setLineDash([]);
  }, [rendered, trace, traceB, currentTime, mode, height, windowSec, showTargets]);

  return (
    <canvas
      ref={ref}
      className="w-full rounded-xl bg-gradient-to-b from-white to-brand-50/50 ring-1 ring-brand-100/80"
      style={{ height }}
    />
  );
}
